import { useEffect, useRef } from "react";
import {
  BinocularsIcon,
  CheckCircleIcon,
  SealCheckIcon,
  UserCirclePlusIcon,
  type Icon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { trackOnboardingSignUp } from "@/analytics/onboardingAnalytics";
import OnboardingActionButton from "../components/OnboardingActionButton";
import OnboardingModal from "../components/OnboardingModal";
import { ONBOARDING_STEP_HEADING_ID } from "../components/OnboardingStepHeader";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingSurface } from "../state/useOnboardingSurface";

const BENEFIT_KEYS = [
  "onboardingJumpInBenefit1",
  "onboardingJumpInBenefit2",
  "onboardingJumpInBenefit3",
  "onboardingJumpInBenefit4",
];

const CARD =
  "rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default p-3";

/** The 40px tile each card leads with. Decorative: its heading says it. */
const CardIcon = ({ icon: IconComponent }: { icon: Icon }) => (
  <span
    aria-hidden="true"
    className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-container-neutral-minimal"
  >
    <IconComponent size={20} weight="fill" className="text-fg-brand-subtle" />
  </span>
);

/**
 * "Let’s jump in!" (`15666:345509`) — signed-out only, straight after the
 * city. The desktop port of native's `AllSetSheet`, with its copy.
 *
 * Two cards rather than two buttons, as on native: signing up is presented
 * with what it unlocks, and "I’ll do it later" is a card of equal standing,
 * because carrying on without an account is a real answer, not a dismissal.
 *
 * Both answers end the flow. "Sign up now" ends it before opening the auth
 * modal, whose email-first screen is sign-in and sign-up both: the city and
 * the other answers are already recorded, and a new account inherits them -
 * @see `useMarkNewUserForOnboarding` - so there is nothing left to ask after.
 *
 * No footer: the frame puts the only filled button inside the first card.
 */
const JumpInStep = () => {
  const i18n = useI18N();
  const { close, dismiss, isEngaged } = useOnboardingFlow();
  const { presentation } = useOnboardingSurface();
  const { showModal } = useAuthModals();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const title = i18n.get("onboardingJumpInTitle");

  // Same rule as `OnboardingStepHeader`, which this screen does not use: the
  // docked sheet only takes focus once the visitor is working through it.
  useEffect(() => {
    if (presentation !== "docked" || isEngaged) headingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = () => {
    trackOnboardingSignUp();
    close();
    showModal(AuthModalScreen.EnterEmail);
  };

  return (
    <OnboardingModal onClose={dismiss} ariaLabel={title}>
      {/* Pinned to the bottom of the sheet, as the frame draws it: the
          sheet is a fixed height and this is shorter than it. */}
      <div className="flex min-h-full flex-col items-center justify-end gap-4">
        <SealCheckIcon
          size={40}
          weight="fill"
          aria-hidden="true"
          className="shrink-0 text-fg-neutral-strong"
        />

        <div className="flex w-full flex-col text-center">
          <h2
            ref={headingRef}
            id={ONBOARDING_STEP_HEADING_ID}
            tabIndex={-1}
            className="title-m-bold text-fg-neutral-strong outline-none"
          >
            {title}
          </h2>
          <p className="label-s-regular text-fg-neutral-subtle">
            {i18n.get("onboardingJumpInBody")}
          </p>
        </div>

        <section
          aria-labelledby="onboarding-jump-in-sign-up"
          className={`${CARD} flex w-full flex-col gap-3`}
        >
          <div className="flex items-center gap-3">
            <CardIcon icon={UserCirclePlusIcon} />
            <h3
              id="onboarding-jump-in-sign-up"
              className="body-l-semibold min-w-0 flex-1 text-fg-neutral-strong"
            >
              {i18n.get("onboardingJumpInSignUpTitle")}
            </h3>
          </div>

          <div className="flex flex-col gap-5">
            <ul className="flex flex-col gap-2">
              {BENEFIT_KEYS.map((key) => (
                <li key={key} className="flex items-center gap-2 px-2.5">
                  <CheckCircleIcon
                    size={16}
                    weight="fill"
                    aria-hidden="true"
                    className="shrink-0 text-fg-neutral-strong"
                  />
                  <span className="label-s-medium min-w-0 flex-1 text-fg-neutral-strong">
                    {i18n.get(key)}
                  </span>
                </li>
              ))}
            </ul>

            <OnboardingActionButton
              variant="primary"
              label={i18n.get("onboardingJumpInSignUp")}
              withArrow
              onClick={signUp}
              className="w-full"
            />
          </div>
        </section>

        {/* The whole card is the target: the design gives it a title and a
            line under it, more than a button label holds. */}
        <button
          type="button"
          onClick={close}
          className={`${CARD} focus-blue flex w-full items-center gap-3 text-left transition-colors hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active`}
        >
          <CardIcon icon={BinocularsIcon} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="body-l-semibold text-fg-neutral-strong">
              {i18n.get("onboardingJumpInLaterTitle")}
            </span>
            <span className="label-s-regular text-fg-brand-subtle">
              {i18n.get("onboardingJumpInLaterBody")}
            </span>
          </span>
        </button>
      </div>
    </OnboardingModal>
  );
};

export default JumpInStep;
